import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import SecretarioLayout from '../secretario/layout'
import { AVAILABLE, CATEGORIES, GOOGLE_SCOPES, TOOL_COUNT, categoryStats, connectorsOf, plural } from '../secretario/catalog'
import Icon from '../components/icon'
import ChatMockup from '../components/chat-mockup'
import { SITE, WA_SECRETARIO } from '../site'

// Conversación de la demostración del hero; las burbujas aparecen una tras otra.
const HERO_CHAT = [
  { from: 'user', text: 'Agenda una reunión con Ana el jueves a las 10 y envíale la invitación', time: '7:52' },
  {
    from: 'bot',
    text: 'Propongo agendar «Reunión con Ana» el jueves de 10:00 a 10:45 a. m., con videollamada, e invitarla. Para hacerlo, respóndeme «sí».',
    time: '7:52',
  },
  { from: 'user', text: 'sí', time: '7:53' },
  { from: 'bot', text: 'Listo. Quedó en tu calendario y Ana recibió la invitación.', time: '7:53' },
]

// Qué resuelve: las tres ideas de la campaña de Secretario.
const PILLARS = [
  {
    icon: 'chat',
    title: 'Basta un mensaje',
    problem: 'Saltar entre calendario, correo, tareas y documentos para cada cosa pequeña.',
    text: 'Le escribes o le mandas una nota de voz en tu WhatsApp de siempre. No hay app que instalar ni comandos que aprender.',
  },
  {
    icon: 'workflow',
    title: 'Entiende y ejecuta',
    problem: 'Pendientes que se quedan anotados en la cabeza y nunca llegan a la agenda.',
    text: 'Entiende qué, cuándo y con quién, y lo hace en tus propias cuentas: agenda, envía, anota y comparte.',
  },
  {
    icon: 'shield',
    title: 'Tú tienes el control',
    problem: 'Miedo a que un asistente haga cosas en tu nombre sin preguntarte.',
    text: 'Antes de cambiar algo te muestra exactamente qué va a hacer y espera tu «sí». Las consultas las resuelve solo.',
  },
]

const BENEFITS = [
  { icon: 'mic', title: 'Texto o voz', text: 'Escríbele o háblale. Transcribe tus notas de voz y las atiende como un mensaje.' },
  { icon: 'clock', title: 'Sigue trabajando por ti', text: 'Recordatorios y encargos a una hora, una vez o cada semana, aunque no le escribas.' },
  { icon: 'sliders', title: 'Aprende tu estilo', text: '«De ahora en adelante, mis correos formales»: guarda tus preferencias y las aplica.' },
  { icon: 'lock', title: 'Tus datos protegidos', text: 'Autorizaciones cifradas y nada de entrenar modelos de IA con tu información.' },
]

const STEPS = [
  {
    title: 'Solicita acceso',
    text: 'Escríbenos por WhatsApp. Durante el acceso anticipado registramos tu número para que Secretario te atienda.',
  },
  {
    title: 'Conecta tus herramientas',
    text: 'La primera vez que uses un conector, Secretario te envía un enlace seguro para autorizarlo en el servicio.',
  },
  {
    title: 'Pídele lo que necesites',
    text: 'Como a una persona, por mensaje o nota de voz. Antes de actuar, te pide tu «sí».',
  },
]

const CONTROLS = [
  {
    icon: 'shield',
    title: 'Nada sin tu «sí»',
    text: 'Cualquier acción que cambie algo espera tu confirmación, y vence si no llega a tiempo.',
  },
  {
    icon: 'logout',
    title: 'Desconecta cuando quieras',
    text: 'Pídele que desconecte una cuenta: revocamos el acceso en el servicio y borramos la autorización.',
  },
  {
    icon: 'clock',
    title: 'Memoria corta',
    text: 'Recuerda tus últimos mensajes para entender el contexto y los olvida tras un tiempo sin conversación.',
  },
  {
    icon: 'lock',
    title: 'Datos protegidos',
    text: 'Las autorizaciones se guardan cifradas y todo viaja por conexiones seguras.',
  },
]

const FAQ = [
  {
    q: '¿Tengo que instalar algo?',
    a: 'No. Secretario funciona dentro de WhatsApp: le escribes al número de Secretario como a cualquier contacto.',
  },
  {
    q: '¿Secretario puede leer mis correos?',
    a: 'Hoy no. Envía los correos que le pides, pero no lee, busca ni borra los mensajes de tu bandeja de entrada.',
  },
  {
    q: '¿Puedo enviarle notas de voz?',
    a: 'Sí. Secretario transcribe la nota de voz y la atiende como si la hubieras escrito. Fotos, videos, documentos y stickers todavía no.',
  },
  {
    q: '¿Con qué herramientas funciona?',
    a: 'Con los conectores disponibles, que encuentras en Funcionalidades junto con lo que puede hacer cada uno. Vamos sumando conectores nuevos.',
  },
  {
    q: '¿Dónde aprendo a usar cada herramienta?',
    a: 'En «Aprende a usar Secretario» encuentras cada conector con sus herramientas, ejemplos para copiar y una guía paso a paso.',
  },
  {
    q: '¿Cómo dejo de usarlo?',
    a: 'Pídele que desconecte tus cuentas para quitarle el acceso y pídenos por WhatsApp o por correo que demos de baja tu número. Al darte de baja borramos tu registro, tus autorizaciones y tus avisos programados.',
  },
  {
    q: '¿Cuánto cuesta?',
    a: 'Secretario está en acceso anticipado. Escríbenos y te contamos las condiciones.',
  },
]

// Datos estructurados para buscadores.
const JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Secretario',
  applicationCategory: 'ProductivityApplication',
  operatingSystem: 'WhatsApp',
  url: `${SITE.url}/secretario`,
  description: 'Asistente de IA en WhatsApp que agenda reuniones, envía correos, crea tareas, documentos y recordatorios con las herramientas que ya usas.',
  publisher: { '@type': 'Organization', name: SITE.brand, url: SITE.url },
}

const Secretario = () => (
  <SecretarioLayout>
    <Helmet>
      <title>Secretario · Tu asistente personal en WhatsApp | Variant</title>
      <meta
        name="description"
        content="Secretario es un asistente de IA en WhatsApp que agenda reuniones, envía correos, crea tareas, documentos y recordatorios con las herramientas que ya usas, siempre con tu confirmación."
      />
      <link rel="canonical" href={`${SITE.url}/secretario`} />
      <meta property="og:title" content="Secretario · Tu asistente personal en WhatsApp" />
      <meta property="og:url" content={`${SITE.url}/secretario`} />
      <meta name="theme-color" content="#0e100f" />
      <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
    </Helmet>

    <section className="sec-hero">
      <div className="container sec-hero__grid">
        <div>
          <span className="sec-pill">
            <span className="sec-pill__dot" aria-hidden="true" /> Acceso anticipado
          </span>
          <h1 className="sec-hero__title">
            Tú escribes.
            <br />
            <span>Secretario se encarga.</span>
          </h1>
          <p className="sec-hero__lead">
            Tu asistente personal en WhatsApp. Pídele por mensaje o nota de voz que agende reuniones, envíe correos,
            anote tareas o arme un documento, y lo resuelve con las herramientas que ya usas después de tu «sí».
          </p>
          <div className="btn-row">
            <Link to="/secretario#comenzar" className="sec-btn sec-btn--accent">
              Comenzar ahora <Icon name="arrow" size={18} />
            </Link>
            <Link to="/secretario/funcionalidades" className="sec-btn sec-btn--glass">
              Explorar funcionalidades
            </Link>
          </div>
          <dl className="sec-hero__stats">
            <div>
              <dt>Conectores</dt>
              <dd>{AVAILABLE.length}</dd>
            </div>
            <div>
              <dt>Herramientas</dt>
              <dd>{TOOL_COUNT}</dd>
            </div>
            <div>
              <dt>Apps por instalar</dt>
              <dd>0</dd>
            </div>
          </dl>
        </div>
        <div className="sec-hero__demo">
          <ChatMockup messages={HERO_CHAT} />
        </div>
      </div>
      <div className="container">
        <ul className="sec-strip" aria-label="Qué puede hacer Secretario">
          {CATEGORIES.filter((c) => connectorsOf(c.id).length).map((c) => (
            <li key={c.id}>
              <Link to={`/secretario/funcionalidades#${c.id}`}>
                <span className="sec-strip__icon">
                  <Icon name={c.icon} size={16} />
                </span>
                {c.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section className="sec-section">
      <div className="container">
        <div className="sec-head">
          <span className="sec-eyebrow">Qué es Secretario</span>
          <h2 className="sec-title">No es otro chat. Es alguien que trabaja para ti.</h2>
          <p className="sec-lead">
            Secretario es un agente de inteligencia artificial que entiende lo que le pides en lenguaje natural y usa tus
            herramientas para hacerlo, sin que tengas que abrirlas.
          </p>
        </div>
        <div className="sec-pillars">
          {PILLARS.map((p, i) => (
            <article key={p.title} className="sec-pillar">
              <span className="sec-pillar__num">0{i + 1}</span>
              <Icon name={p.icon} size={24} className="sec-pillar__icon" />
              <h3>{p.title}</h3>
              <p className="sec-pillar__problem">{p.problem}</p>
              <p>{p.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="sec-section sec-section--tint" id="funciones">
      <div className="container">
        <div className="sec-head sec-head--row">
          <div>
            <span className="sec-eyebrow">Funcionalidades</span>
            <h2 className="sec-title">Lo que harías en varias aplicaciones, en un solo chat</h2>
          </div>
          <Link to="/secretario/funcionalidades" className="sec-link">
            Ver todas las funcionalidades <Icon name="arrow" size={16} />
          </Link>
        </div>
        <div className="sec-cats">
          {CATEGORIES.map((cat) => {
            const items = connectorsOf(cat.id)
            if (!items.length) return null
            return (
              <Link key={cat.id} to={`/secretario/funcionalidades#${cat.id}`} className="sec-cat">
                <span className="sec-cat__icon">
                  <Icon name={cat.icon} size={22} />
                </span>
                <h3>{cat.name}</h3>
                <p>{cat.blurb}</p>
                <span className="sec-cat__foot">
                  {plural(items.length, 'conector', 'conectores')} · {plural(categoryStats(cat.id).tools, 'herramienta')}
                  <Icon name="chevron" size={16} />
                </span>
              </Link>
            )
          })}
        </div>
      </div>
    </section>

    <section className="sec-section">
      <div className="container">
        <div className="sec-head">
          <span className="sec-eyebrow">Beneficios</span>
          <h2 className="sec-title">Pensado para tu día, no para tu pantalla</h2>
        </div>
        <div className="sec-benefits">
          {BENEFITS.map((b) => (
            <div key={b.title} className="sec-benefit">
              <Icon name={b.icon} size={22} />
              <h3>{b.title}</h3>
              <p>{b.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="sec-section sec-section--dark">
      <div className="container sec-learn-cta">
        <div>
          <span className="sec-eyebrow sec-eyebrow--light">Centro de ayuda</span>
          <h2 className="sec-title">Aprende a usar Secretario</h2>
          <p className="sec-lead">
            Cada conector con sus herramientas, ejemplos listos para copiar y una guía paso a paso. Abierto para todos, sin
            iniciar sesión.
          </p>
          <div className="btn-row">
            <Link to="/secretario/aprende" className="sec-btn sec-btn--accent">
              <Icon name="book" size={18} /> Ir al centro de ayuda
            </Link>
          </div>
        </div>
        <ol className="sec-learn-cta__list">
          {CATEGORIES.filter((c) => connectorsOf(c.id).length).map((c) => (
            <li key={c.id}>
              <Link to={`/secretario/aprende/${connectorsOf(c.id)[0].id}`}>
                <span className="sec-strip__icon sec-strip__icon--lg">
                  <Icon name={c.icon} size={18} />
                </span>
                <span>
                  <strong>{c.name}</strong>
                  <small>{plural(categoryStats(c.id).tools, 'herramienta')} con ejemplos</small>
                </span>
                <Icon name="chevron" size={18} />
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="sec-section sec-section--tint" id="permisos">
      <div className="container">
        <div className="sec-head">
          <span className="sec-eyebrow">Privacidad y permisos</span>
          <h2 className="sec-title">Qué datos usa Secretario y para qué</h2>
          <p className="sec-lead">
            Cada conector pide solo los permisos que necesita, y Secretario los usa únicamente para cumplir lo que le
            pides. Estos son los permisos de los conectores de Google:
          </p>
        </div>
        <div className="scopes">
          <div className="scopes__row scopes__row--head" aria-hidden="true">
            <span>Permiso</span>
            <span>Para qué lo usa Secretario</span>
          </div>
          {GOOGLE_SCOPES.map((s) => (
            <div key={s.scope} className="scopes__row">
              <div>
                <strong>{s.products.join(', ')}</strong>
                <span className="scopes__access">{s.access}</span>
                <code>{s.scope}</code>
              </div>
              <p>{s.use}</p>
            </div>
          ))}
        </div>
        <div className="limited-use">
          <Icon name="shield" size={22} />
          <div>
            <p>
              El uso y la transferencia a cualquier otra aplicación de la información recibida de las API de Google por
              parte de Secretario se ajustan a la{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
                Política de datos de usuario de los servicios de las API de Google
              </a>
              , incluidos los requisitos de Uso Limitado.
            </p>
            <p className="limited-use__en" lang="en">
              Secretario's use and transfer to any other app of information received from Google APIs will adhere to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements.
            </p>
            <p>
              No vendemos tus datos, no los usamos para publicidad ni para entrenar modelos de IA. Los detalles están en la{' '}
              <Link to="/politica-de-privacidad">política de privacidad</Link>.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="sec-section" id="control">
      <div className="container">
        <div className="sec-head">
          <span className="sec-eyebrow">Tu control</span>
          <h2 className="sec-title">Tú decides qué hace y qué recuerda</h2>
        </div>
        <div className="sec-benefits">
          {CONTROLS.map((c) => (
            <div key={c.title} className="sec-benefit">
              <Icon name={c.icon} size={22} />
              <h3>{c.title}</h3>
              <p>{c.text}</p>
            </div>
          ))}
        </div>
        <p className="sec-note">
          Puedes revisar o quitar el acceso de Secretario en cualquier momento desde la configuración de seguridad de
          cada servicio conectado.
        </p>
      </div>
    </section>

    <section className="sec-section sec-section--tint" id="comenzar">
      <div className="container">
        <div className="sec-start">
          <div className="sec-start__intro">
            <span className="sec-eyebrow">Comenzar ahora</span>
            <h2 className="sec-title">Empieza en tres pasos</h2>
            <p className="sec-lead">
              Estamos abriendo cupos de acceso anticipado. Secretario solo atiende números registrados: escríbenos y te
              damos de alta.
            </p>
            <div className="btn-row">
              <a href={WA_SECRETARIO} className="sec-btn sec-btn--accent" target="_blank" rel="noopener noreferrer">
                <Icon name="chat" size={18} /> Solicitar acceso por WhatsApp
              </a>
              <a href={`mailto:${SITE.email}?subject=Acceso%20a%20Secretario`} className="sec-btn sec-btn--line">
                <Icon name="mail" size={18} /> Escribir un correo
              </a>
            </div>
          </div>
          <ol className="sec-steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <span className="sec-steps__num">{i + 1}</span>
                <div>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>

    <section className="sec-section" id="preguntas">
      <div className="container">
        <div className="sec-head sec-head--center">
          <span className="sec-eyebrow">Preguntas frecuentes</span>
          <h2 className="sec-title">Lo que suelen preguntarnos</h2>
        </div>
        <div className="faq">
          {FAQ.map((f) => (
            <details key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  </SecretarioLayout>
)

export default Secretario
